import React, { useEffect, useRef } from 'react';
import { X, CreditCard } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /** Classes pour la zone de contenu (ex. scroll, hauteur max) */
  bodyClassName?: string;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = 'md',
  bodyClassName = '',
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Fermer la modal avec la touche Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    
    // Désactiver le scroll du body quand la modal est ouverte
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  // Fermer la modal quand on clique à l'extérieur
  const handleOutsideClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const maxWidthClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-xl',
    xl: 'max-w-2xl',
    '2xl': 'max-w-4xl',
  };

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
      onClick={handleOutsideClick}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <div
        ref={modalRef}
        className={`bg-[#2F3136] rounded-lg shadow-xl w-full ${maxWidthClasses[maxWidth]} mx-4 transform transition-all duration-300 ease-in-out`}
        style={{ position: 'relative', zIndex: 999999 }}
      >
        <div className="bg-[#5865F2] px-4 py-3 sm:p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {typeof title === 'string' ? (
                <>
                  <CreditCard className="w-6 h-6 text-white mr-2" />
                  <h3 className="text-2xl font-bold text-white">{title}</h3>
                </>
              ) : (
                <h3 className="text-2xl font-bold text-white">{title}</h3>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className={`p-4 sm:p-5 ${bodyClassName}`.trim()}>
          {children}
        </div>
      </div>
    </div>
  );
}; 